/*
 * Warns before an editor is abandoned with work still unsaved.
 *
 * The panel's editors are full pages, and drawing a route or placing a stop on
 * the map is minutes of work. A stray click on the browser's back button, a
 * mistyped address in the bar, or closing the tab threw all of it away without
 * a word. This guards both ways out: navigating inside the app, and leaving the
 * page entirely.
 *
 * A component using it implements snapshotForm(), returning whatever counts as
 * the form's contents, and calls markClean() once the record has loaded and
 * again after a successful save:
 *
 *   import unsavedChanges from "@/mixins/unsavedChanges";
 *
 *   mixins: [unsavedChanges],
 *   methods: {
 *     snapshotForm() { return { name: this.stop.name, address: this.stop.address }; },
 *   }
 *
 * Until markClean() runs the first time the guard stays quiet, so a page that
 * fails to load never traps the admin on it.
 */
export default {
  data() {
    return {
      //the form as it was when last saved or loaded, serialised for comparison
      cleanSnapshot: null,
    };
  },
  mounted() {
    window.addEventListener("beforeunload", this.warnIfUnsaved);
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.warnIfUnsaved);
  },
  beforeRouteLeave(to, from, next) {
    if (!this.hasUnsavedChanges()) {
      next();
      return;
    }

    this.$confirmAction({
      title: this.$t("unsaved_changes"),
      text: this.$t("unsaved_changes_text"),
      confirmText: this.$t("leave_without_saving"),
      cancelText: this.$t("keep_editing"),
    }).then((confirmed) => {
      //next(false) cancels the navigation and leaves the editor as it was
      next(confirmed ? undefined : false);
    });
  },
  methods: {
    //call once the record is loaded, and again after every successful save
    markClean() {
      this.cleanSnapshot = this.serialiseForm();
    },
    serialiseForm() {
      if (typeof this.snapshotForm !== "function") return null;
      try {
        return JSON.stringify(this.snapshotForm());
      } catch (e) {
        //a form we cannot serialise is one we cannot compare, so say nothing
        return null;
      }
    },
    hasUnsavedChanges() {
      if (this.cleanSnapshot === null) return false;
      const current = this.serialiseForm();
      return current !== null && current !== this.cleanSnapshot;
    },
    //the browser only shows its own wording here, and only if we set this
    warnIfUnsaved(event) {
      if (!this.hasUnsavedChanges()) return undefined;
      event.preventDefault();
      event.returnValue = "";
      return "";
    },
  },
};
